import {autoUpdate, flip, offset, shift, useFloating} from '@floating-ui/react-dom';
import React, {Fragment, useEffect, useRef, useState} from "react";
import {Popover, PopoverButton, PopoverPanel, Transition,} from "@headlessui/react";
import {ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon,} from "@heroicons/react/20/solid";
import {CalendarDaysIcon} from "@heroicons/react/24/outline"; // Or use your custom icon

const getMonths = () => [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
];

const getYears = (currentYear, range = 50) => {
    return Array.from({length: range + 1}, (_, i) => currentYear + i);
};

export const CustomDatePicker = (props) => {
    const {label, selectedDate, setSelectedDate} = props;

    const today = new Date();
    const buttonRef = useRef();
    const panelRef = useRef();

    const {x, y, strategy, refs, update} = useFloating({
        placement: 'bottom-start',
        middleware: [offset(({placement}) => {
            return placement.startsWith("top") ? 16 : 0;
        }), flip(), shift()],
        // middleware: [offset(4), flip(), shift()],
    });

    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [hovered, setHovered] = useState(null);

    const months = getMonths();
    const currentYear = new Date().getFullYear();
    const years = getYears(currentYear);

    const getDaysInMonth = () => {
        const days = new Date(selectedYear, selectedMonth + 1, 0).getDate();
        const startDay = new Date(selectedYear, selectedMonth, 1).getDay();
        const array = Array.from({length: startDay}, () => null);
        for (let i = 1; i <= days; i++) {
            array.push(i);
        }
        return array;
    };

    const handleDayClick = async (day, closePopover) => {
        const date = new Date(selectedYear, selectedMonth, day);
        setSelectedDate(date); // Set selected date
        closePopover();
        // await handleDateChange(date);
    };

    const isToday = (day) => {
        const now = new Date();
        return (
            day === now.getDate() &&
            selectedMonth === now.getMonth() &&
            selectedYear === now.getFullYear()
        );
    };

    useEffect(() => {
        if (refs.reference.current && refs.floating.current) {
            return autoUpdate(refs.reference.current, refs.floating.current, update);
        }
    }, [refs.reference, refs.floating, update]);

    return (
        <div className="flex items-center justify-center">
            <Popover className="relative inline-block">
                {({open, close}) => (
                    <>
                        {/* Calendar Trigger Button */}
                        <PopoverButton
                            ref={(el) => {
                                buttonRef.current = el;
                                refs.setReference(el);
                            }}
                            className="flex items-center gap-2 px-3 py-2 border border-gray-300 outline-none
                                portrait:text-[1.25rem] portrait:md:text-[1rem]
                                portrait:jk:text-[1.2rem] portrait:lg:text-[1.5rem]
                                landscape:text-[0.8rem] landscape:jk:text-[0.9rem] landscape:lg:text-[1rem]
                                leading-tight font-custom-semi-bold text-gray-900
                                bg-white  shadow-lg shadow-black/5
                                landscape:rounded-md portrait:rounded-md portrait:jk:rounded-lg"
                        >
                            <CalendarDaysIcon className="w-5 h-5 text-gray-600"/>
                            {selectedDate ? selectedDate.toLocaleDateString('en-GB').split('/').join('-') : label || "Pick a Date"}
                        </PopoverButton>

                        {/* Calendar Panel */}
                        <Transition
                            as={Fragment}
                            show={open}
                            enter="transition ease-out duration-200"
                            enterFrom="opacity-0 translate-y-1"
                            enterTo="opacity-100 translate-y-0"
                            leave="transition ease-in duration-150"
                            leaveFrom="opacity-100 translate-y-0"
                            leaveTo="opacity-0 translate-y-1"
                        >
                            <PopoverPanel
                                ref={(el) => {
                                    panelRef.current = el;
                                    refs.setFloating(el);
                                }}
                                style={{position: strategy, top: y ?? 0, left: x ?? 0}}
                                static
                                className="absolute z-20 mt-2 w-72 bg-white rounded-xl shadow-xl ring-1 ring-orange-300/5"
                            >
                                <div className="p-4">
                                    {/* Header Controls */}
                                    <div className="flex justify-between items-center mb-3">
                                        <button
                                            onClick={() => {
                                                if (selectedMonth === 0) {
                                                    setSelectedMonth(11);
                                                    setSelectedYear((y) => y - 1);
                                                } else {
                                                    setSelectedMonth((m) => m - 1);
                                                }
                                            }}
                                        >
                                            <ChevronLeftIcon className="w-5 h-5 text-gray-600"/>
                                        </button>

                                        <div className="flex gap-2 items-center">
                                            {/* Month Popover */}
                                            <Popover className="relative">
                                                <PopoverButton
                                                    onMouseEnter={() => setHovered("month")}
                                                    onMouseLeave={() => setHovered(null)}
                                                    className="text-sm px-2 py-1 border rounded flex items-center gap-1"
                                                >
                                                    {months[selectedMonth]}
                                                    <ChevronDownIcon
                                                        className={`w-4 h-4 ${
                                                            hovered === "month" ? "rotate-180" : ""
                                                        }`}
                                                    />
                                                </PopoverButton>
                                                <Transition
                                                    as={Fragment}
                                                    show={hovered === "month"}
                                                    enter="transition ease-out duration-100"
                                                    enterFrom="opacity-0"
                                                    enterTo="opacity-100"
                                                    leave="transition ease-in duration-100"
                                                    leaveFrom="opacity-100"
                                                    leaveTo="opacity-0"
                                                >
                                                    <PopoverPanel
                                                        onMouseEnter={() => setHovered("month")}
                                                        onMouseLeave={() => setHovered(null)}
                                                        className="absolute z-10 mt-1 max-h-48 overflow-y-auto bg-white border rounded shadow w-28"
                                                    >
                                                        {months.map((month, idx) => {
                                                            const isFutureMonth =
                                                                selectedYear === today.getFullYear() && idx > today.getMonth();

                                                            return (
                                                                <div
                                                                    key={month}
                                                                    onClick={() => {
                                                                        if (!isFutureMonth) {
                                                                            setSelectedMonth(idx);
                                                                            setHovered(null);
                                                                        }
                                                                    }}
                                                                    className={`px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer
                                                                    ${isFutureMonth ? "text-gray-400 cursor-not-allowed pointer-events-none" : ""}`}
                                                                >
                                                                    {month}
                                                                </div>
                                                            )
                                                        })}
                                                    </PopoverPanel>
                                                </Transition>
                                            </Popover>

                                            {/* Year Popover */}
                                            <Popover className="relative">
                                                <PopoverButton
                                                    onMouseEnter={() => setHovered("year")}
                                                    onMouseLeave={() => setHovered(null)}
                                                    className="text-sm px-2 py-1 border rounded flex items-center gap-1"
                                                >
                                                    {selectedYear}
                                                    <ChevronDownIcon
                                                        className={`w-4 h-4 ${
                                                            hovered === "year" ? "rotate-180" : ""
                                                        }`}
                                                    />
                                                </PopoverButton>
                                                <Transition
                                                    as={Fragment}
                                                    show={hovered === "year"}
                                                    enter="transition ease-out duration-100"
                                                    enterFrom="opacity-0"
                                                    enterTo="opacity-100"
                                                    leave="transition ease-in duration-100"
                                                    leaveFrom="opacity-100"
                                                    leaveTo="opacity-0"
                                                >
                                                    <PopoverPanel
                                                        onMouseEnter={() => setHovered("year")}
                                                        onMouseLeave={() => setHovered(null)}
                                                        className="absolute z-10 mt-1 max-h-48 overflow-y-auto bg-white border rounded shadow w-24"
                                                    >
                                                        {years.map((year) => {
                                                            const isDisabled = year > new Date().getFullYear();
                                                            return (
                                                                <div
                                                                    key={year}
                                                                    onClick={() => {
                                                                        if (!isDisabled) {
                                                                            setSelectedYear(year);
                                                                            setHovered(null);
                                                                        }
                                                                    }}
                                                                    className={`px-4 py-2 text-sm ${
                                                                        isDisabled
                                                                            ? "text-gray-400 cursor-not-allowed"
                                                                            : "hover:bg-gray-100 cursor-pointer"
                                                                    }`}
                                                                >
                                                                    {year}
                                                                </div>
                                                            );
                                                        })}
                                                    </PopoverPanel>
                                                </Transition>
                                            </Popover>
                                        </div>

                                        <button
                                            onClick={() => {
                                                if (selectedMonth === 11) {
                                                    setSelectedMonth(0);
                                                    setSelectedYear((y) => y + 1);
                                                } else {
                                                    setSelectedMonth((m) => m + 1);
                                                }
                                            }}
                                        >
                                            <ChevronRightIcon className="w-5 h-5 text-gray-600"/>
                                        </button>
                                    </div>

                                    {/* Weekdays */}
                                    <div className="grid grid-cols-7 text-xs text-center text-gray-500 mb-1">
                                        {["S", "M", "T", "W", "T", "F", "S"].map((d) => (
                                            <div key={d}>{d}</div>
                                        ))}
                                    </div>

                                    {/* Dates */}
                                    <div className="grid grid-cols-7 gap-1 text-center text-sm">
                                        {getDaysInMonth().map((day, idx) => {
                                            const today = new Date();

                                            const isFuture =
                                                day &&
                                                (selectedYear > today.getFullYear() ||
                                                    (selectedYear === today.getFullYear() &&
                                                        (selectedMonth > today.getMonth() ||
                                                            (selectedMonth === today.getMonth() && day > today.getDate()))));

                                            const isSelected =
                                                selectedDate &&
                                                day === selectedDate.getDate() &&
                                                selectedMonth === selectedDate.getMonth() &&
                                                selectedYear === selectedDate.getFullYear();

                                            return (
                                                <div
                                                    key={idx}
                                                    onClick={() => day && !isFuture && handleDayClick(day, close)}
                                                    className={`h-9 flex items-center justify-center rounded cursor-pointer
                                                        ${day ? "hover:bg-blue-100" : ""}
                                                        ${isToday(day) ? "bg-blue-300" : ""}
                                                        ${isSelected ? "bg-blue-500 text-white font-bold" : ""}
                                                        ${isFuture ? "text-gray-400 cursor-not-allowed pointer-events-none" : ""}
                                                    `}
                                                >
                                                    {day || ""}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </PopoverPanel>
                        </Transition>
                    </>
                )}
            </Popover>
        </div>
    );
};
